import { getConfig } from '../../config';
import { BookingReference, Order } from '../../types/Order';
import { getBookingMethod } from '../utils/payments';

import { ActivityActions } from './activities';

const { batchWebURL, fareharborDashboard } = getConfig();

export const ORDERS_CSV_HEADERS = [
  { label: 'Order Name', key: 'name' },
  { label: 'BAT Order ID', key: 'id' },
  { label: 'Status', key: 'status' },
  { label: 'Order Placed Date', key: 'orderPlacedDate' },
  { label: 'Customer Email', key: 'customerEmail' },
  { label: 'Customer Name', key: 'customerName' },
  { label: 'Customer Phone', key: 'customerPhone' },
  { label: 'Requested Start Date', key: 'requestedStartDate' },
  { label: 'Requested Start Time', key: 'requestedStartTime' },
  { label: 'Fulfillment Date', key: 'fulfillmentDate' },
  { label: 'Subtotal', key: 'subtotalPrice' },
  { label: 'Taxes', key: 'totalTax' },
  { label: 'Total', key: 'totalPrice' },
  { label: 'Discount Code', key: 'discountCode' },
  { label: 'Discount Amount', key: 'discountAmount' },
  { label: 'Vendor ID', key: 'vendorId' },
  { label: 'Vendor Name', key: 'vendorName' },
  { label: 'Experience Name', key: 'activityName' },
  { label: 'Booking Option Title(s)', key: 'bookingOptionTitles' },
  { label: 'Quantity', key: 'quantity' },
  { label: 'Party ID', key: 'partyId' },
  { label: 'Party Name', key: 'partyName' },
  { label: 'Party Size', key: 'partySize' },
  { label: 'Activity ID', key: 'activityId' },
];

export const ADDITIONAL_ORDERS_QA_LABELS = new Map([
  [
    'notes-needed',
    'Any preferences or special requests you’d like to share with our experience partner?',
  ],
  [
    'additional-location-needed',
    'If your desired ending location is different from your pick-up location, please enter it here:',
  ],
  [
    'dietary-needed',
    'Please let our team know of any allergies or dietary restrictions:',
  ],
]);

export const getOrderActions = (order: Order): ActivityActions[] => {
  const { id: orderId } = order;

  return [
    {
      label: 'View on web',
      image: { src: '/copy-square.png', alt: 'bach-web-link', width: '20px' },
      type: 'link',
      href: `${batchWebURL}/orders/${orderId}`,
      show: !!orderId,
    },
    {
      label: 'Copy order deeplink',
      image: { src: '/copy-icon.svg', alt: 'page-deep-link', width: '20px' },
      type: 'button',
      href: `${batchWebURL}/orders/${orderId}`,
      show: !!orderId,
    },
  ];
};

export const DiscountType = {
  fixed_amount: 'fixed_amount',
  percentage: 'percentage',
} as const;

type BookingReferenceBuilder = (
  booking: Order['booking']
) => BookingReference | undefined;

const getFareharborBookingReferenceBuilder = (
  baseUrl: string
): BookingReferenceBuilder => (
  booking: Order['booking']
): BookingReference | undefined => {
  if (!booking) return undefined;

  return {
    identifier: booking.display_id,
    url: `${baseUrl}/bookings/network/${booking.company.shortname}${new URL(
      booking.dashboard_url
    ).searchParams.get('overlay')}`,
  };
};

const octoBookingReferenceBuilder = (booking: Order['booking']) => {
  if (!booking) return undefined;

  return {
    identifier: booking.supplierReference,
  };
};

const bookingReferenceBuilders = {
  fareharbor: getFareharborBookingReferenceBuilder(fareharborDashboard),
  peek: octoBookingReferenceBuilder,
  singenuity: octoBookingReferenceBuilder,
};

export const getBookingReference = (
  order: Pick<Order, 'activity' | 'booking'>
): BookingReference | undefined => {
  const { activity } = order;

  let integration: string | undefined;
  if (activity.meta) {
    const bookingMethod = getBookingMethod(activity.meta.capabilities);
    integration = bookingMethod?.kind;
  }

  if (!integration) integration = activity.source;
  if (!integration) return undefined;

  const builder = bookingReferenceBuilders[integration];
  if (!builder) return undefined;

  return builder(order.booking);
};
